import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import { t } from '../../utils/i18n';
import { formatDateTime } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { getCelebBubbles } from '../../api/authAdmin';
import PaginatedTableCard from '../../components/PaginatedTableCard/PaginatedTableCard';
import UserDetailModal from '../../components/UserDetailModal/UserDetailModal';
import UserBanDialog from '../../components/UserBanDialog/UserBanDialog';
import { useUserBanActions } from '../../utils/useUserBanActions';

const PAGE_SIZE = 10;

const TABLE_SKELETON_WIDTHS = ['w-36', 'w-48', 'w-16', 'w-28', 'w-28', 'w-28'];

function getStatusMeta(status) {
  const value = Number(status ?? 0);
  if (value === 1) {
    return {
      label: t('celebBubble.status.active'),
      className: 'bg-amber-500/15 text-amber-300 border-amber-500/35 shadow-[0_0_12px_rgba(245,158,11,0.12)]',
    };
  }
  if (value === 2) {
    return {
      label: t('celebBubble.status.expired'),
      className: 'bg-dark-surface text-dark-text-secondary border-dark-border',
    };
  }
  if (value === 3) {
    return {
      label: t('celebBubble.status.replaced'),
      className: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    };
  }
  return {
    label: t('celebBubble.status.unknown'),
    className: 'bg-dark-surface text-dark-text-muted border-dark-border',
  };
}

function StatusBadge({ status }) {
  const meta = getStatusMeta(status);
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap border ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function UserCell({ row, onOpen }) {
  const username = row.username ?? '—';
  const name = row.name || '';
  const initial = (name || username || '?').toString().trim().charAt(0).toUpperCase() || '?';
  const userId = row.user_id ?? row.UserID;
  const canOpen = userId != null && Number(userId) >= 1;

  const content = (
    <>
      <span className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/30 to-primary/5 border border-primary/25 flex items-center justify-center text-primary text-sm font-semibold shrink-0">
        {initial}
      </span>
      <div className="min-w-0 text-left">
        <p className="text-sm font-medium text-primary truncate group-hover:underline underline-offset-2">{username}</p>
        <p className="text-xs text-dark-text-secondary truncate">
          #{userId}
          {name ? ` · ${name}` : ''}
        </p>
      </div>
    </>
  );

  if (!canOpen) {
    return <div className="flex items-center gap-3 min-w-0">{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(Number(userId))}
      className="group flex items-center gap-3 min-w-0 max-w-full rounded-lg -mx-1 px-1 py-0.5 transition-colors hover:bg-primary/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/50"
    >
      {content}
    </button>
  );
}

function BubbleMessageCell({ message }) {
  const text = message || '—';
  return (
    <div className="group relative max-w-xs sm:max-w-sm md:max-w-md">
      <div className="inline-flex items-start gap-2 rounded-2xl rounded-bl-md bg-gradient-to-br from-primary/20 via-primary/10 to-dark-surface/80 border border-primary/25 px-3.5 py-2.5 shadow-sm transition-all duration-300 group-hover:border-primary/45 group-hover:shadow-[0_0_20px_rgba(255,192,0,0.12)] group-hover:-translate-y-0.5">
        <MessageCircle size={14} className="mt-0.5 shrink-0 text-primary/80" />
        <p className="text-sm text-dark-text leading-snug break-words">{text}</p>
      </div>
    </div>
  );
}

export default function CelebBubblePage() {
  const navigate = useNavigate();
  const { token, role, cacheSession, logout } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const [page, setPage] = useState(1);
  const [userSearchDraft, setUserSearchDraft] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [messageSearchDraft, setMessageSearchDraft] = useState('');
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [excludeResigned, setExcludeResigned] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);

  const bubblesQuery = useQuery({
    queryKey: [
      'celeb-bubbles',
      apiEnvironment,
      page,
      PAGE_SIZE,
      userSearchQuery,
      messageSearchQuery,
      excludeResigned,
      statusFilter,
    ],
    queryFn: () =>
      getCelebBubbles(page, PAGE_SIZE, token, {
        search: userSearchQuery,
        message: messageSearchQuery,
        excludeResigned,
        status: statusFilter === '' ? undefined : Number(statusFilter),
      }),
    enabled: !!token,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (bubblesQuery.error?.status === 401) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [bubblesQuery.error, logout, navigate]);

  const {
    items: rawItems,
    total_count: totalCount = 0,
    page: currentPage = 1,
    page_size: pageSize = PAGE_SIZE,
  } = bubblesQuery.data || {};
  // API may return items: null (Go nil slice JSON) — treat as [].
  const rows = Array.isArray(rawItems) ? rawItems : [];

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setUserSearchQuery(userSearchDraft.trim());
    setMessageSearchQuery(messageSearchDraft.trim());
  };

  const handleSearchClear = () => {
    setUserSearchDraft('');
    setUserSearchQuery('');
    setMessageSearchDraft('');
    setMessageSearchQuery('');
    setPage(1);
  };

  const handleModalUnauthorized = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleUserOpen = (userId) => {
    if (userId == null || Number(userId) < 1) return;
    setSelectedUserId(Number(userId));
  };

  const closeModal = () => setSelectedUserId(null);

  const { runBanAction, isPending: isBanPending, pendingUserId, dialog, confirmDialog, closeDialog } =
    useUserBanActions({
      token,
      apiEnvironment,
      role,
      cacheSession,
      onUnauthorized: handleModalUnauthorized,
    });

  return (
    <div className="min-h-screen py-8 px-3 sm:px-5 lg:px-6 bg-dark-bg relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-[radial-gradient(ellipse_at_top,_rgba(255,192,0,0.08),_transparent_65%)]"
      />
      <div className="w-full max-w-[min(100%,1920px)] mx-auto relative">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-dark-text flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-primary/15 border border-primary/30 text-primary">
                <MessageCircle size={18} />
              </span>
              {t('celebBubble.title')}
            </h1>
            <p className="text-dark-text-secondary text-sm mt-1">{t('celebBubble.description')}</p>
          </div>
        </div>

        {bubblesQuery.isError && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {bubblesQuery.error?.message}
          </div>
        )}

        <form
          onSubmit={handleSearchSubmit}
          className="mb-4 rounded-xl border border-dark-border bg-gradient-to-br from-dark-card via-dark-card to-dark-surface/50 p-4 shadow-sm"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label htmlFor="celeb-bubble-user-search" className="block text-xs font-medium tracking-wide text-dark-text-secondary uppercase">
                {t('celebBubble.userSearch.label')}
              </label>
              <div className="mt-2 flex min-h-[2.75rem] w-full min-w-0 overflow-hidden rounded-lg border border-dark-border bg-dark-bg shadow-inner transition-colors focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/35">
                <input
                  id="celeb-bubble-user-search"
                  type="search"
                  value={userSearchDraft}
                  onChange={(e) => setUserSearchDraft(e.target.value)}
                  placeholder={t('celebBubble.userSearch.placeholder')}
                  className="min-w-0 flex-1 border-0 bg-transparent py-2.5 pl-4 pr-2 text-sm text-dark-text placeholder:text-dark-text-secondary/60 outline-none focus:ring-0"
                  autoComplete="off"
                />
              </div>
              <p className="mt-2 text-xs leading-relaxed text-dark-text-secondary/90">{t('celebBubble.userSearch.hint')}</p>
            </div>
            <div>
              <label htmlFor="celeb-bubble-message-search" className="block text-xs font-medium tracking-wide text-dark-text-secondary uppercase">
                {t('celebBubble.messageSearch.label')}
              </label>
              <div className="mt-2 flex min-h-[2.75rem] w-full min-w-0 overflow-hidden rounded-lg border border-dark-border bg-dark-bg shadow-inner transition-colors focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/35">
                <input
                  id="celeb-bubble-message-search"
                  type="search"
                  value={messageSearchDraft}
                  onChange={(e) => setMessageSearchDraft(e.target.value)}
                  placeholder={t('celebBubble.messageSearch.placeholder')}
                  className="min-w-0 flex-1 border-0 bg-transparent py-2.5 pl-4 pr-2 text-sm text-dark-text placeholder:text-dark-text-secondary/60 outline-none focus:ring-0"
                  autoComplete="off"
                />
              </div>
              <p className="mt-2 text-xs leading-relaxed text-dark-text-secondary/90">{t('celebBubble.messageSearch.hint')}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-primary/90"
            >
              {t('celebBubble.search.submit')}
            </button>
            <button
              type="button"
              onClick={handleSearchClear}
              className="inline-flex items-center rounded-lg border border-dark-border bg-dark-surface/60 px-4 py-2 text-sm text-dark-text-secondary transition-colors hover:border-primary/40 hover:text-primary"
            >
              {t('celebBubble.search.clear')}
            </button>
          </div>
        </form>

        <div className="mb-4 rounded-xl border border-dark-border bg-dark-card p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <label className="text-xs text-dark-text-secondary flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={excludeResigned}
              onChange={(e) => {
                setExcludeResigned(e.target.checked);
                setPage(1);
              }}
              className="accent-primary"
            />
            <span>{t('celebBubble.filter.excludeResigned')}</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-dark-text-secondary">{t('celebBubble.filter.status')}</span>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-2 py-1.5 rounded-lg bg-dark-bg border border-dark-border text-dark-text text-xs focus:outline-none focus:border-primary/60"
            >
              <option value="">{t('celebBubble.filter.statusAll')}</option>
              <option value="1">{t('celebBubble.filter.statusActive')}</option>
              <option value="2">{t('celebBubble.filter.statusExpired')}</option>
              <option value="3">{t('celebBubble.filter.statusReplaced')}</option>
            </select>
          </div>
        </div>

        <PaginatedTableCard
          error={bubblesQuery.error}
          dataUpdatedAt={bubblesQuery.dataUpdatedAt}
          isPending={bubblesQuery.isPending}
          isFetching={bubblesQuery.isFetching}
          isEmpty={!bubblesQuery.isPending && rows.length === 0}
          skeletonRows={PAGE_SIZE}
          skeletonCellWidths={TABLE_SKELETON_WIDTHS}
          colSpan={6}
          minHeight="min-h-[32rem]"
          tableClassName="w-full min-w-[900px]"
          loadingLabel={t('celebBubble.table.loading') || t('common.loading')}
          emptyLabel={t('celebBubble.table.empty')}
          page={page}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setPage}
          prevLabel={t('celebBubble.pagination.prev')}
          nextLabel={t('celebBubble.pagination.next')}
          summary={t('celebBubble.pagination.page', { page: currentPage, totalPages })}
          header={(
            <tr className="border-b border-dark-border">
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celebBubble.table.user')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celebBubble.table.message')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('celebBubble.table.status')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm whitespace-nowrap">{t('celebBubble.table.visibleAt')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm whitespace-nowrap">{t('celebBubble.table.expiredAt')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm whitespace-nowrap">{t('celebBubble.table.createdAt')}</th>
            </tr>
          )}
        >
          {rows.map((row, index) => (
            <tr
              key={row.id ?? `${row.user_id}-${row.created_at}-${index}`}
              className="border-b border-dark-border/70 last:border-0 hover:bg-dark-surface/40 transition-colors duration-200 animate-[fadeSlideIn_0.35s_ease-out_both]"
              style={{ animationDelay: `${Math.min(index, 8) * 35}ms` }}
            >
              <td className="py-3.5 px-4 align-middle">
                <UserCell row={row} onOpen={handleUserOpen} />
              </td>
              <td className="py-3.5 px-4 align-middle">
                <BubbleMessageCell message={row.message} />
              </td>
              <td className="py-3.5 px-4 align-middle">
                <StatusBadge status={row.status} />
              </td>
              <td className="py-3.5 px-4 align-middle text-sm text-dark-text-secondary whitespace-nowrap">
                {formatDateTime(row.visible_at)}
              </td>
              <td className="py-3.5 px-4 align-middle text-sm text-dark-text-secondary whitespace-nowrap">
                {formatDateTime(row.expired_at)}
              </td>
              <td className="py-3.5 px-4 align-middle text-sm text-dark-text-secondary whitespace-nowrap">
                {formatDateTime(row.created_at)}
              </td>
            </tr>
          ))}
        </PaginatedTableCard>

        {selectedUserId && token && (
          <UserDetailModal
            userId={selectedUserId}
            token={token}
            onClose={closeModal}
            onUnauthorized={handleModalUnauthorized}
            onNavigateUserId={setSelectedUserId}
            initialUser={rows.find((r) => Number(r.user_id ?? r.UserID) === Number(selectedUserId))}
            runBanAction={runBanAction}
            isBanPending={isBanPending}
            pendingUserId={pendingUserId}
          />
        )}

        <UserBanDialog
          dialog={dialog}
          onConfirm={confirmDialog}
          onCancel={closeDialog}
          loading={isBanPending}
        />
      </div>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
